import { useState, useMemo } from 'react'
import {MutedText} from '@/components/shared/MutedText'
import {MonoText} from '@/components/shared/MonoText'
import {Stack} from '@/components/shared/Stack'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {
  Database,
  Check,
  X,
  Pencil,
} from 'lucide-react'
import { ApiError } from '@/api/client'
import {useServices} from '@/services/context'
import { useI18n } from '@/i18n'
import { useAuth } from '@/contexts/auth-context'
import { permissions } from '@/lib/permissions'
import { routes } from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { useToast } from '@/hooks/use-toast'
import { AttributeEntityType } from '@/types/attribute-entity-type'
import { IconPicker } from '@/components/entity-type/icon-picker'
import { ColorPicker } from '@/components/entity-type/color-picker'
import { resolveEntityTypeIcon } from '@/components/entity-type/entity-type-icon'
import {PageLayout} from '@/components/shared/PageLayout'
import { RowCardHeader } from '@/components/shared/RowCardHeader'
import { Icon } from '@/components/shared/Icon'
import { InlineRow } from '@/components/shared/InlineRow'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import { type ColorOption } from '@/components/entity-type/entity-type-badge'
import {LargeDialog} from '@/components/shared/LargeDialog'
import {AssociationEditor} from '@/components/shared/AssociationEditor'
import type {AssociationColumn} from '@/components/shared/AssociationEditor'
import {StatusText} from '@/components/shared/StatusText'
import type {
  TypeValue,
  UpdateTranslationsRequest,
  TypeValueSettings,
  UpdateSettingsRequest,
} from '@/types/types'
import type { AttributeDefinition, UpdateAttributeDefinitionRequest } from '@/types/attribute-definition'

// Tables that support displayNameTemplate

const TEMPLATE_TABLES = new Set(['identity_types', 'party_types'])

// Tables that have scoped attributes
const SCOPED_ATTR_TABLES = new Set(['party_types', 'identity_types', 'resource_types'])

// Map from type table to attribute entity type
const TABLE_TO_ATTR_ENTITY: Record<string, AttributeEntityType> = {
  identity_types: AttributeEntityType.IDENTITY,
  party_types: AttributeEntityType.PARTY,
  resource_types: AttributeEntityType.RESOURCE,
}


function parseSettings(raw: Record<string, unknown> | null | undefined): TypeValueSettings {
  if (!raw) return {}
  return {
    icon: typeof raw.icon === 'string' ? raw.icon : undefined,
    color: typeof raw.color === 'string' ? raw.color : undefined,
    displayNameTemplate: typeof raw.displayNameTemplate === 'string' ? raw.displayNameTemplate : undefined,
  }
}

// ============================================================
// Main page component
// ============================================================

export function TypeValueDetail() {
    const services = useServices()
  const { tableName, code } = useParams<{ tableName: string; code: string }>()
  const i18n = useI18n()

  const queryClient = useQueryClient()
  const { hasPermission } = useAuth()
  const { toast } = useToast()

  const canView = hasPermission(permissions.types.view)
  const canUpdate = hasPermission(permissions.types.update)
  const canUpdateAttrDefs = hasPermission(permissions.attributeDefinitions.update)

  // Edit translations dialog state
  const editTranslationsDialog = useDialog()
  const [editTranslations, setEditTranslations] = useState<Record<string, { label: string; description: string }>>({})

  // Settings form state
  const [settingsIcon, setSettingsIcon] = useState<string | null | undefined>(undefined)
  const [settingsColor, setSettingsColor] = useState<ColorOption>('default')
  const [settingsTemplate, setSettingsTemplate] = useState<string>('')
  const [settingsLoaded, setSettingsLoaded] = useState(false)
  const [settingsVersion, setSettingsVersion] = useState<number>(0)

  // Track whether settings form is dirty
  const [savedSettings, setSavedSettings] = useState<TypeValueSettings>({})

  // Scoped attribute search state
  const [attrDefSearch, setAttrDefSearch] = useState('')

  // Fetch locales
  const { data: localesData } = useQuery({
    queryKey: ['types', 'locales'],
    queryFn: () => services.types.locales(),
    enabled: canView,
  })

  // Fetch type table (to get the specific type value)
  const {
    data: typeTable,
    isLoading: isLoadingTable,
    error: tableError,
  } = useQuery({
    queryKey: ['types', tableName],
    queryFn: () => services.types.detail(tableName!),
    enabled: !!tableName && canView,
    retry: false,
  })

  // Fetch attribute definitions for the scoped attributes card and template chips
  const attrEntityType = tableName ? TABLE_TO_ATTR_ENTITY[tableName] : undefined
  const { data: attrDefsData } = useQuery({
    queryKey: ['attribute-definitions', attrEntityType],
    queryFn: () => services.attributeDefinitions.list(attrEntityType!),
    enabled: !!attrEntityType && canView,
  })

  // eslint-disable-next-line i18next/no-literal-string
  const locales = localesData?.locales || ['en']

  // Find the specific type value
  const typeValue = useMemo(() => {
    return typeTable?.values.find((v) => v.code === code) ?? null
  }, [typeTable, code])

  // Initialize settings form once data loads
  useMemo(() => {
    if (!settingsLoaded && typeValue) {
      const s = parseSettings(typeValue.settings as Record<string, unknown> | null)
      setSettingsIcon(s.icon ?? null)
      setSettingsColor((s.color as ColorOption) ?? 'default')
      setSettingsTemplate(s.displayNameTemplate ?? '')
      setSavedSettings(s)
      setSettingsVersion(typeValue.version)
      setSettingsLoaded(true)
    }
  }, [settingsLoaded, typeValue])

  // Filter attribute definitions available for this type: global (empty typeCodes) + type-scoped
  const scopedAttrs = useMemo(() => {
    if (!attrDefsData || !code) return []
    return attrDefsData.filter((def) => def.typeCodes.length === 0 || def.typeCodes.includes(code))
  }, [attrDefsData, code])

  // Stale placeholder warning
  const staleWarning = useMemo(() => {
    if (!settingsTemplate || !TEMPLATE_TABLES.has(tableName ?? '')) return null
    const placeholders = Array.from(settingsTemplate.matchAll(/\{([^}]+)\}/g)).map(m => m[1])
    const attrNames = new Set(scopedAttrs.map((a) => a.name))
    const stale = placeholders.filter((p) => !attrNames.has(p))
    if (stale.length === 0) return null
    return i18n.types.settingsTemplateStalePlaceholders({ placeholders: stale.join(', ') })
  }, [settingsTemplate, tableName, scopedAttrs, i18n])

  // Search results for adding attribute definitions to scope
  const attrDefSearchResults = useMemo(() => {
    if (!attrDefsData || !code || !attrDefSearch) return []
    const scopedIds = new Set(scopedAttrs.map(a => a.id))
    return attrDefsData.filter(def =>
      !scopedIds.has(def.id) &&
      !def.typeCodes.includes(code) &&
      (def.name.toLowerCase().includes(attrDefSearch.toLowerCase()) ||
       def.displayName.toLowerCase().includes(attrDefSearch.toLowerCase()))
    )
  }, [attrDefsData, code, attrDefSearch, scopedAttrs])

  // Mutation: add type code to an attribute definition's scope
  const addScopeMutation = useEntityMutation({
    mutationFn: (def: AttributeDefinition) => {
      const req: UpdateAttributeDefinitionRequest = {
        name: def.name,
        displayName: def.displayName,
        valueType: def.valueType,
        authorityId: def.authorityId ?? undefined,
        operations: def.operations,
        validationRules: def.validationRules ?? undefined,
        required: def.required,
        sortOrder: def.sortOrder,
        active: def.active,
        searchable: def.searchable,
        filterable: def.filterable,
        version: def.version,
        typeCodes: [...def.typeCodes, code!],
      }
      return services.attributeDefinitions.update(attrEntityType!, def.id, req)
    },
    invalidate: ['attribute-definitions', attrEntityType],
    onError: (err: ApiError) => {
      if (err.status === 409) {
        toast({ variant: 'destructive', description: i18n.associationEditor.conflictToast })
        queryClient.invalidateQueries({ queryKey: ['attribute-definitions', attrEntityType] })
      }
    },
  })

  // Mutation: remove type code from an attribute definition's scope
  const removeScopeMutation = useEntityMutation({
    mutationFn: (def: AttributeDefinition) => {
      const req: UpdateAttributeDefinitionRequest = {
        name: def.name,
        displayName: def.displayName,
        valueType: def.valueType,
        authorityId: def.authorityId ?? undefined,
        operations: def.operations,
        validationRules: def.validationRules ?? undefined,
        required: def.required,
        sortOrder: def.sortOrder,
        active: def.active,
        searchable: def.searchable,
        filterable: def.filterable,
        version: def.version,
        typeCodes: def.typeCodes.filter(tc => tc !== code),
      }
      return services.attributeDefinitions.update(attrEntityType!, def.id, req)
    },
    invalidate: ['attribute-definitions', attrEntityType],
    onError: (err: ApiError) => {
      if (err.status === 409) {
        toast({ variant: 'destructive', description: i18n.associationEditor.conflictToast })
        queryClient.invalidateQueries({ queryKey: ['attribute-definitions', attrEntityType] })
      }
    },
  })

  // Column definitions for the scoped attributes AssociationEditor
  /* eslint-disable i18next/no-literal-string -- className values are CSS, not user-facing text */
  const scopedAttrColumns: AssociationColumn<AttributeDefinition>[] = useMemo(() => [
    { header: String(i18n.types.scopedAttributesName), render: (a: AttributeDefinition) => <MonoText>{a.name}</MonoText> },
    { header: String(i18n.types.scopedAttributesDisplayName), render: (a: AttributeDefinition) => a.displayName },
    { header: String(i18n.types.scopedAttributesValueType), render: (a: AttributeDefinition) => a.valueType },
    {
      header: String(i18n.types.scopedAttributesRequired),
      className: 'text-center',
      render: (a: AttributeDefinition) => a.required
        ? <Icon icon={Check} className="mx-auto text-status-success" />
        : <Icon icon={X} className="mx-auto" muted />,
    },
  ], [i18n])
  /* eslint-enable i18next/no-literal-string */

  // Update translations mutation
  const updateTranslationsMutation = useEntityMutation({
    mutationFn: ({ data }: { data: UpdateTranslationsRequest }) =>
      services.types.updateTranslations(tableName!, code!, data),
    invalidate: ['types', tableName],
    closeDialog: editTranslationsDialog.close,
  })

  // Update settings mutation
  const updateSettingsMutation = useMutation<TypeValue, ApiError, UpdateSettingsRequest>({
    mutationFn: (data: UpdateSettingsRequest) =>
      services.types.updateSettings(tableName!, code!, data), onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['types', tableName] })
      const newSettings = parseSettings(updated.settings as Record<string, unknown> | null)
      setSavedSettings(newSettings)
      setSettingsVersion(updated.version)
      toast({ description: i18n.types.settingsSaveSuccess })
      const hasTemplate = TEMPLATE_TABLES.has(tableName ?? '')
      const templateChanged = newSettings.displayNameTemplate !== savedSettings.displayNameTemplate
      if (hasTemplate && templateChanged) {
        toast({ description: i18n.types.settingsSaveSuccessRederivation })
      }
    },
    onError: (err: ApiError) => {
      if (err.status === 409) {
        toast({
          variant: 'destructive',
          description: i18n.types.settingsConflict,
        })
        queryClient.invalidateQueries({ queryKey: ['types', tableName] })
        setSettingsLoaded(false)
      }
    },
  })

  const openEditTranslations = () => {
    const initial: Record<string, { label: string; description: string }> = {}
    locales.forEach((locale) => {
      const t = typeValue?.translations[locale]
      initial[locale] = { label: t?.label ?? '', description: t?.description ?? '' }
    })
    setEditTranslations(initial)
    editTranslationsDialog.open()
  }

  const handleSaveTranslations = () => {
    updateTranslationsMutation.mutate({
      data: {
        translations: editTranslations as Record<string, { label: string; description: string | null }>,
      },
    })
  }

  const handleSaveSettings = () => {
    const settingsPayload: UpdateSettingsRequest['settings'] = {
      icon: settingsIcon ?? null,
      color: settingsColor,
    }
    if (TEMPLATE_TABLES.has(tableName ?? '')) {
      settingsPayload.displayNameTemplate = settingsTemplate || null
    }
    updateSettingsMutation.mutate({ version: settingsVersion, settings: settingsPayload })
  }

  const appendChip = (attrName: string) => {
    setSettingsTemplate((prev) => prev + `{${attrName}}`)
  }

  const DND_MIME = 'text/plain' as const
  const MEASURER_CLASS = 'invisible absolute whitespace-pre' as const

  const handleChipDragStart = (e: React.DragEvent<HTMLButtonElement>, attrName: string) => {
    e.dataTransfer.setData(DND_MIME, `{${attrName}}`)
    e.dataTransfer.effectAllowed = 'copy'
  }

  const getCaretPositionFromX = (input: HTMLInputElement, clientX: number): number => {
    const inputRect = input.getBoundingClientRect()
    const style = getComputedStyle(input)
    const relativeX = clientX - inputRect.left - parseFloat(style.paddingLeft)
    let pos = input.value.length
    const measurer = document.createElement('span')
    measurer.style.font = style.font
    measurer.className = MEASURER_CLASS
    document.body.appendChild(measurer)
    for (let i = 0; i <= input.value.length; i++) {
      measurer.textContent = input.value.slice(0, i)
      if (measurer.offsetWidth >= relativeX) {
        pos = i
        break
      }
    }
    document.body.removeChild(measurer)
    return pos
  }

  const handleTemplateDragOver = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    const input = e.currentTarget
    input.focus()
    const pos = getCaretPositionFromX(input, e.clientX)
    input.setSelectionRange(pos, pos)
  }

  const handleTemplateDrop = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault()
    const text = e.dataTransfer.getData(DND_MIME)
    if (!text) return
    const input = e.currentTarget
    const dropPos = getCaretPositionFromX(input, e.clientX)
    const newValue = input.value.slice(0, dropPos) + text + input.value.slice(dropPos)
    setSettingsTemplate(newValue)
    requestAnimationFrame(() => {
      input.focus()
      const newCursor = dropPos + text.length
      input.selectionStart = newCursor
      input.selectionEnd = newCursor
    })
  }

  // ============================================================
  // Resolved data (computed before render, guarded by loading/error)
  // ============================================================

  const settings = !isLoadingTable && typeValue ? parseSettings(typeValue.settings as Record<string, unknown> | null) : {}
  const ResolvedIcon = resolveEntityTypeIcon(settings.icon)
  const IconComponent = ResolvedIcon ?? Database
  const translatedLabel = typeValue
    ? (typeValue.translations[locales[0]]?.label
      ?? Object.values(typeValue.translations)[0]?.label
      ?? code)
    : code

  const showTemplateEditor = TEMPLATE_TABLES.has(tableName ?? '')
  const showScopedAttrs = SCOPED_ATTR_TABLES.has(tableName ?? '')

  const backTarget = routes.schema.types.detail(tableName ?? '')

  return (
    <PageLayout
      data-testid="type-value-detail-page"
      variant="detail"
      title={code ?? i18n.types.valueDetailPageTitle}
      description={typeValue ? translatedLabel : undefined}
      icon={IconComponent}
      loading={isLoadingTable}
      error={tableError ?? (!isLoadingTable ? notFoundIfMissing(typeValue, 'TYPE_VALUE') : undefined)}
      backTo={backTarget}
    >
      {typeValue && <>
      <div data-testid="type-value-detail">

        {/* Translations Card */}
        <Card>
          <RowCardHeader>
            <CardTitle data-testid="card-title-translations">{i18n.types.translations}</CardTitle>
            {canUpdate && (
              <Button
                variant="outline"
                size="sm"
                onClick={openEditTranslations}
                data-testid="btn-edit-translations"
              >
                <Icon icon={Pencil} className="mr-2" />
                {i18n.common.edit}
              </Button>
            )}
          </RowCardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead data-testid="col-language" className="w-20">{i18n.types.language}</TableHead>
                  <TableHead data-testid="col-label">{i18n.types.label}</TableHead>
                  <TableHead data-testid="col-description">{i18n.common.description}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locales.map((locale) => {
                  const t = typeValue.translations[locale]
                  return (
                    <TableRow key={locale}>
                      <TableCell className="font-medium uppercase">{locale}</TableCell>
                      <TableCell>
                        {t?.label ?? <MutedText className="italic">{'\u2014'}</MutedText>}
                      </TableCell>
                      <TableCell>
                        {t?.description ?? <MutedText className="italic">{'\u2014'}</MutedText>}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Scoped Attribute Definitions Card — before Settings so attributes are available for template chips */}
        {showScopedAttrs && (
          <Card>
            <CardHeader>
              <CardTitle data-testid="card-title-scoped-attributes">{i18n.types.scopedAttributesCard}</CardTitle>
            </CardHeader>
            <CardContent>
              <AssociationEditor<AttributeDefinition>
                items={scopedAttrs}
                onSearch={setAttrDefSearch}
                searchResults={attrDefSearchResults}
                onAdd={(def) => addScopeMutation.mutate(def)}
                onRemove={(def) => removeScopeMutation.mutate(def)}
                columns={scopedAttrColumns}
                getKey={(def) => def.id}
                getLabel={(def) => `${def.name} — ${def.displayName}`}
                emptyMessage={String(i18n.types.scopedAttributesEmpty)}
                disabled={!canUpdateAttrDefs}
                testId="scoped-attributes"
              />
            </CardContent>
          </Card>
        )}

        {/* Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle data-testid="card-title-settings">{i18n.types.settingsCard}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* Icon picker */}
            <Stack gap="sm">
              <Label>{i18n.types.settingsIconLabel}</Label>
              <IconPicker
                value={settingsLoaded ? settingsIcon : settings.icon}
                onChange={canUpdate ? setSettingsIcon : () => {}}
                disabled={!canUpdate}
              />
            </Stack>

            {/* Color picker */}
            <Stack gap="sm">
              <Label>{i18n.types.settingsColorLabel}</Label>
              <ColorPicker
                value={settingsLoaded ? settingsColor : (settings.color ?? 'default')}
                onChange={canUpdate ? (c) => setSettingsColor(c as ColorOption) : () => {}}
                disabled={!canUpdate}
              />
            </Stack>

            {/* Display name template (identity_types, party_types only) */}
            {showTemplateEditor && (
              <Stack gap="sm">
                <Label>{i18n.types.settingsTemplateLabel}</Label>
                <Input
                  value={settingsLoaded ? settingsTemplate : (settings.displayNameTemplate ?? '')}
                  onChange={(e) => setSettingsTemplate(e.target.value)}
                  onDragOver={canUpdate ? handleTemplateDragOver : undefined}
                  onDrop={canUpdate ? handleTemplateDrop : undefined}
                  placeholder={i18n.types.settingsTemplatePlaceholder}
                  disabled={!canUpdate}
                  data-testid="input-template"
                />
                {staleWarning && (
                  <StatusText tone="warning" data-testid="stale-placeholder-warning">{staleWarning}</StatusText>
                )}
                {canUpdate && (
                  <Stack gap="sm">
                    <MutedText size="xs">{i18n.types.settingsTemplateChipsHint}</MutedText>
                    {scopedAttrs.length === 0 ? (
                      <MutedText size="sm">{i18n.types.settingsTemplateNoAttributes}</MutedText>
                    ) : (
                      <InlineRow wrap gap="sm">
                        {scopedAttrs.map((attr) => (
                          <Button
                            key={attr.id}
                            variant="outline"
                            size="sm"
                            draggable
                            onClick={() => appendChip(attr.name)}
                            onDragStart={(e) => handleChipDragStart(e, attr.name)}
                            className="cursor-grab"
                            data-testid={`chip-draggable-${attr.name}`}
                          >
                            {`{${attr.name}}`}
                          </Button>
                        ))}
                      </InlineRow>
                    )}
                  </Stack>
                )}
              </Stack>
            )}

            {/* Empty state note */}
            {!settings.icon && !settings.color && !settings.displayNameTemplate && (
              <MutedText size="sm">{i18n.types.settingsCardEmpty}</MutedText>
            )}

            {/* Save button */}
            {canUpdate && (
              <InlineRow justify="end" className="pt-2">
                <Button
                  onClick={handleSaveSettings}
                  disabled={updateSettingsMutation.isPending}
                  data-testid="btn-save-settings"
                >
                  {updateSettingsMutation.isPending ? i18n.common.saving : i18n.common.save}
                </Button>
              </InlineRow>
            )}
          </CardContent>
        </Card>

      {/* Edit Translations Dialog */}
      <Dialog open={editTranslationsDialog.isOpen} onOpenChange={editTranslationsDialog.bind.onOpenChange}>
        <LargeDialog>
          <DialogHeader>
            <DialogTitle>{i18n.types.editTranslations}</DialogTitle>
            <DialogDescription>
              {i18n.types.editTranslationsFor({ code: code ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead data-testid="col-language-edit" className="w-20">{i18n.types.language}</TableHead>
                <TableHead data-testid="col-label-edit">{i18n.types.label}</TableHead>
                <TableHead data-testid="col-description-edit">{i18n.common.description}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locales.map((locale) => (
                <TableRow key={locale}>
                  <TableCell className="font-medium uppercase">{locale}</TableCell>
                  <TableCell>
                    <Input
                      value={editTranslations[locale]?.label ?? ''}
                      onChange={(e) =>
                        setEditTranslations((prev) => ({
                          ...prev,
                          [locale]: { ...prev[locale], label: e.target.value },
                        }))
                      }
                      placeholder={i18n.types.label}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={editTranslations[locale]?.description ?? ''}
                      onChange={(e) =>
                        setEditTranslations((prev) => ({
                          ...prev,
                          [locale]: { ...prev[locale], description: e.target.value },
                        }))
                      }
                      placeholder={i18n.types.descriptionOptional}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <SaveCancelFooter
            testIdPrefix="edit-translations"
            onCancel={editTranslationsDialog.close}
            saving={updateTranslationsMutation.isPending}
            disabled={locales.some((l) => !editTranslations[l]?.label)}
            saveType="button"
            onSave={handleSaveTranslations}
          />
        </LargeDialog>
      </Dialog>

      </div>
    </>}
    </PageLayout>
  )
}
